import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import QrScanner from 'react-qr-scanner';
import { IconButton, Button, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import FlashOffIcon from "@mui/icons-material/FlashOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import "../styles/QRCodeMealClaim.css"; // Đảm bảo bạn có file CSS này
import { API_ENDPOINTS } from "../config/api"; // Đảm bảo đường dẫn này đúng
import scanSound from "../sound/scan.mp3";
import failSound from "../sound/fail.mp3";
import notRegisSound from "../sound/notregis.mp3";

// --- Cấu hình hiển thị kết quả ---
const resultConfig = {
  success: {
    color: "#28a745",
    icon: <CheckCircleIcon sx={{ fontSize: 80 }} />,
    title: "XÁC NHẬN THÀNH CÔNG",
  },
  error: {
    color: "#dc3545",
    icon: <ErrorIcon sx={{ fontSize: 80 }} />,
    title: "KHÔNG TÌM THẤY SUẤT ĂN",
  },
  warning: {
    color: "#ffc107",
    icon: <WarningAmberIcon sx={{ fontSize: 80 }} />,
    title: "BẠN ĐÃ NHẬN SUẤT ĂN HÔM NAY",
  },
  not_registered: {
    color: "#dc3545",
    icon: <ErrorIcon sx={{ fontSize: 80 }} />,
    title: "BẠN CHƯA ĐĂNG KÝ SUẤT ĂN HÔM NAY",
  }
};

// --- Hàm helper gọi API (đặt bên ngoài vì không dùng hook) ---
async function validateMealQR(location_code, accessToken) {
  const response = await fetch(API_ENDPOINTS.VALIDATEQR, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ location_code }),
  });
  
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.message || `Lỗi HTTP: ${response.status}`);
    error.response = data;
    throw error;
  }
  return data;
}

// --- Component chính ---
export default function QRCodeMealClaim() {
  // --- TẤT CẢ HOOKS ĐƯỢC ĐẶT Ở ĐÂY ---
  const [flash, setFlash] = useState(false);
  const [result, setResult] = useState(null); // { type, info }
  const [isProcessing, setIsProcessing] = useState(false);
  const timerRef = useRef();
  const [audio] = useState(() => new Audio(scanSound));
  const [failAudio] = useState(() => new Audio(failSound));
  const [notRegisAudio] = useState(() => new Audio(notRegisSound));
  const navigate = useNavigate();
  const [pendingSound, setPendingSound] = useState(null);
  const [qrDetected, setQrDetected] = useState(false);

  // --- TẤT CẢ HÀM XỬ LÝ ĐƯỢC ĐẶT Ở ĐÂY ---
  const playSuccessSound = () => {
    audio.currentTime = 0;
    audio.play().catch(error => {
      console.log('Không thể phát âm thanh:', error);
    });
  };

  const playFailSound = () => {
    failAudio.currentTime = 0;
    failAudio.play().catch(error => {
      console.log('Không thể phát âm thanh:', error);
    });
  };

  const playNotRegisSound = () => {
    notRegisAudio.currentTime = 0;
    notRegisAudio.play().catch(error => {
      console.log('Không thể phát âm thanh:', error);
    });
  };

  const handleScan = async (data) => {
    console.log("handleScan called with data:", data);
    if (data && !isProcessing && !result) {
      setIsProcessing(true);
      setQrDetected(true);
      try {
        const location_code = typeof data === 'string' ? data : data.text;
        const accessToken = localStorage.getItem('token');
        console.log("location_code:", location_code);
        console.log("accessToken:", accessToken);
        if (!accessToken) {
          throw new Error("Phiên đăng nhập đã hết hạn.");
        }
        
        const res = await validateMealQR(location_code, accessToken);
        
        let type = "success";
        let message = res.message;
        let time = new Date().toLocaleTimeString();
        if (res.status === "warning") {
          type = "warning";
          if (res.received_at) {
            time = res.received_at;
          }
        }
        if (res.status === "error") {
          type = "error";
          if (res.meal_status === "NOT_REGISTERED" && res.message) {
            message = res.message;
            type = "not_registered";
          }
        }
        setResult({
          type,
          info: { message, time }
        });
        
        if (type === "success") {
          playSuccessSound();
        } else if (type === "not_registered") {
          playNotRegisSound();
        } else {
          playFailSound();
        }
      } catch (err) {
        const status = err.response?.status;
        let type = "error";
        if (status === 'not_found') type = 'warning';
        
        setResult({
          type: type,
          info: { message: err.message || "Lỗi không xác định." }
        });
        
        playFailSound();
      } finally {
        setIsProcessing(false);
        setTimeout(() => setQrDetected(false), 1000);
      }
    }
  };

  const handleError = (err) => {
    setResult({
      type: "error",
      info: {
        message: "Không thể truy cập camera. Vui lòng mở ứng dụng bằng trình duyệt Safari thay vì màn hình chính trên iPhone."
      },
    });
  };
  
  const handleComplete = () => {
    if (pendingSound) {
      playSuccessSound();
      setPendingSound(null);
    }
    navigate('/dashboard');
  };

  // --- USEEFFECT ĐƯỢC ĐẶT Ở ĐÂY ---
  useEffect(() => {
    if (result?.type === 'success') {
      timerRef.current = setTimeout(handleComplete, 30000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [result]);

  useEffect(() => {
    return () => {
      audio.pause();
      audio.currentTime = 0;
      failAudio.pause();
      failAudio.currentTime = 0;
      notRegisAudio.pause();
      notRegisAudio.currentTime = 0;
    };
  }, [audio, failAudio, notRegisAudio]);

  useEffect(() => {
    if (result) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [result]);

  // --- PHẦN RENDER JSX ---
  if (result) {
    const cfg = resultConfig[result.type] || resultConfig.error;
    return (
      <Box
        className="qr-result-screen"
        sx={{
          bgcolor: cfg.color,
          color: "#fff",
          width: "100vw",
          height: "100vh",
          minHeight: "100vh",
          minWidth: "100vw",
          maxWidth: "100vw",
          maxHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          position: "fixed",
          top: 0,
          left: 0,
          padding: 0
        }}
      >
        {cfg.icon}
        <Typography variant="h4" sx={{ mt: 2, fontWeight: "bold", textAlign: 'center' }}>{cfg.title}</Typography>
        <Typography sx={{ mt: 1, textAlign: 'center' }}>{result.info.message}</Typography>
        {result.info.time && <Typography sx={{ mt: 0.5 }}>Thời gian: {result.info.time}</Typography>}
        <Button variant="contained" sx={{ mt: 4, bgcolor: "#fff", color: cfg.color, fontWeight: "bold" }} onClick={handleComplete}>
          Hoàn tất
        </Button>
      </Box>
    );
  }

  return (
    <Box className="qr-scan-container">
      <div className="qr-overlay">
        <div className={`qr-viewfinder${qrDetected ? ' detected' : ''}`}>
          <div className="qr-laser"></div>
        </div>
      </div>
      <IconButton
        className="qr-close-btn"
        onClick={() => navigate('/dashboard')}
        sx={{
          position: "absolute",
          top: 56,
          right: 16,
          color: "#fff",
          zIndex: 2
        }}
      >
        <CloseIcon fontSize="large" />
      </IconButton>
      <IconButton className="qr-flash-btn" onClick={() => setFlash(f => !f)} sx={{ position: "absolute", bottom: 100, color: "#fff", zIndex: 2 }}>
        {flash ? <FlashOnIcon fontSize="large" /> : <FlashOffIcon fontSize="large" />}
      </IconButton>
      <Typography variant="h6" sx={{ position: "absolute", bottom: 40, width: "100%", textAlign: "center", color: "#fff", zIndex: 2, textShadow: "0 2px 8px #000" }}>
        Di chuyển camera vào mã QR
      </Typography>
      {!isProcessing && (
        <QrScanner
          delay={500}
          onError={handleError}
          onScan={handleScan}
          style={{ width: '100vw', height: '100vh' }}
          constraints={{ video: { facingMode: "environment", torch: flash } }}
        />
      )}
    </Box>
  );
}